import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Control, Controller, FieldErrors, useForm, UseFormSetValue, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useWarehouses } from 'src/services/warehouse';
import { zodResolver } from '@hookform/resolvers/zod';
import { useOutsideClick } from 'src/hooks/utils';
import Input, { ControlledInput } from 'src/components/ui/Input/Input';
import Select, { ControlledSelect, Option } from 'src/components/ui/Select/Select';
import Divider from 'src/components/ui/Divider/Divider';
import dayjs from 'dayjs';
import PartnerSelect from 'src/components/layouts/PartnerSelect/PartnerSelect';
import { DatePicker } from 'src/components/ui/DatePicker/DatePicker';
import TablePagination from 'src/components/ui/TablePagination/TablePagination';
import Button, { ButtonPositionProps, ButtonToneProps } from 'src/components/ui/Button/Button';
import Icon, { IconName } from 'src/components/ui/Icon/Icon';
import Tooltip from 'src/components/ui/Tooltip/Tooltip';
import classNames from 'classnames';
import { Partner } from 'src/services/partner';
import ContainerHead from 'src/components/ui/ContainerOverlay/ContainerHead/ContainerHead';
import { t } from 'i18next';
import {
  inboundFormDataStepSchema,
  InboundFormDataStepValues,
  PackageFormValues,
  useInboundFormContext,
} from './InboundFormContext';
import { isInboundFormDirty, serializeInboundFormValues } from './formDirty';
import InboundPackageTable from './InboundPackageTable';
import { getPaginationOptions } from '../../utils/general';
import { InboundOrder } from '../../services/inbound';
import styles from '../styles/Inbound.module.scss';
import InboundSummaryTable from './InboundSummaryTable/InboundSummaryTable';
import InboundPhotoGallery from './InboundPhotoGallery/InboundPhotoGallery';
import ContainerOverlay from '../../components/ui/ContainerOverlay/ContainerOverlay';
import ContainerBody from '../../components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import { SelectProductsFromAllProducts } from '../../components/layouts/SelectProducts/SelectProducts';

export const Section: React.FC<{
  children: React.ReactNode;

  className?: string;
}> = ({ children, className }) => {
  return <div className={`p-24 rounded-base surface-elevated border-subtle ${className ?? ''}`}>{children}</div>;
};

interface SectionHeadlineProps {
  children: React.ReactNode;
  tag?: keyof JSX.IntrinsicElements;
}

export const SectionHeadline: React.FC<SectionHeadlineProps> = ({ children, tag = 'h2' }) => {
  const Tag = tag;
  return <Tag className="typo-title-24">{children}</Tag>;
};

export const ActionBar: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className="flex justify-end gap-x-12">{children}</div>;
};

const PackagesCreatorEmptyCard: React.FC<{
  icon: IconName;
  ctaLabel: string;
  onClickAction?: () => void;
}> = ({ onClickAction, icon, ctaLabel }) => {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={onClickAction}
      style={{ borderStyle: 'dashed' }}
      className="group flex-1 rounded-base border-t-[2px] border-subtle hover:border-primary flex flex-col justify-center items-center py-40 gap-24 bg-surface-selected hover:bg-primary-tint transition-colors cursor-pointer">
      <Icon name={icon} className="text-disabled group-hover:text-primary transition-colors" width={40} height={40} />
      <Button
        title={ctaLabel}
        tone={ButtonToneProps.SECONDARY}
        icon="plus"
        iconPosition={ButtonPositionProps.BEFORE}
        onClick={onClickAction}
      />
    </div>
  );
};

export const PackagesCreatorEmptyState: React.FC<{
  onCreate?: (type: PackageFormValues['packageType']) => void;
}> = ({ onCreate }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-32">
      <PackagesCreatorEmptyCard
        ctaLabel={t('inbound.create_parcel')}
        icon="package"
        onClickAction={() => onCreate?.('parcel')}
      />
      <PackagesCreatorEmptyCard
        ctaLabel={t('inbound.create_pallete')}
        icon="pallet"
        onClickAction={() => onCreate?.('pallet')}
      />
    </div>
  );
};

const PackagesCreatorHeader: React.FC<{
  icon: IconName;
  name?: string;
  deleteButtonLabel: string;
  duplicateButtonLabel: string;
  onDeleteClick?: () => void;
  onDuplicateClick?: () => void;
  onPackageNameChange?: (value: string) => void;
  error?: boolean;
  readOnly?: boolean;
  disableDuplicate?: boolean;
}> = ({
  icon,
  name,
  deleteButtonLabel,
  duplicateButtonLabel,
  onDeleteClick,
  onDuplicateClick,
  onPackageNameChange,
  error = false,
  readOnly,
  disableDuplicate,
}) => {
  const iconName = error ? 'error' : icon;
  return (
    <div
      className={classNames('grid grid-cols-[min-content,1fr,max-content] gap-x-8 items-center', {
        'status-error-full': error,
      })}>
      <Icon width={24} height={24} name={iconName} />
      <Input
        value={name}
        onChange={e => onPackageNameChange?.(e.target.value)}
        isInfoDisplay
        placeholder={`${t('general.name')}`}
      />
      {!readOnly && (
        <div className="flex gap-x-8">
          <Button
            title={duplicateButtonLabel}
            tone={ButtonToneProps.SECONDARY}
            icon="duplicate"
            onClick={onDuplicateClick}
            disabled={disableDuplicate}
          />
          <Button title={deleteButtonLabel} tone={ButtonToneProps.ERROR} onClick={onDeleteClick} />
        </div>
      )}
    </div>
  );
};

const PackagesCreatorEditor: React.FC<{
  onDeleteClick?: () => void;
  onDuplicateClick?: () => void;
  onAddProductClick?: () => void;
  packageType: PackageFormValues['packageType'];
  packageIndex: number;
  control: Control<InboundFormDataStepValues>;
  setValue: UseFormSetValue<InboundFormDataStepValues>;
  errors: FieldErrors<InboundFormDataStepValues>;
  readOnly?: boolean;
}> = ({
  onDeleteClick,
  onDuplicateClick,
  packageType,
  onAddProductClick,
  packageIndex,
  control,
  errors,
  setValue,
  readOnly,
}) => {
  const { t } = useTranslation();

  const config = {
    parcel: {
      icon: 'package' as IconName,
      headline: t('inbound.parcel'),
      deleteButtonLabel: t('inbound.delete_parcel'),
      duplicateButtonLabel: t('inbound.duplicate_parcel'),
    },
    pallet: {
      icon: 'pallet' as IconName,
      headline: t('inbound.pallet'),
      deleteButtonLabel: t('inbound.delete_pallet'),
      duplicateButtonLabel: t('inbound.duplicate_pallet'),
    },
  }[packageType];

  if (!config) {
    throw new Error(`Unknown package type ${packageType}`);
  }

  const lines = useWatch({ control, name: `packages.${packageIndex}.lines` }) ?? [];
  const packageError = errors?.packages?.[packageIndex];
  const linesError = packageError?.lines;

  return (
    <div className="flex flex-col surface-base rounded-base p-24 gap-y-32">
      <Controller
        name={`packages.${packageIndex}.name`}
        control={control}
        render={({ field }) => (
          <PackagesCreatorHeader
            icon={config.icon}
            name={field.value || ''}
            deleteButtonLabel={config.deleteButtonLabel}
            duplicateButtonLabel={config.duplicateButtonLabel}
            onPackageNameChange={field.onChange}
            error={!!linesError}
            readOnly={readOnly}
            onDeleteClick={onDeleteClick}
            onDuplicateClick={onDuplicateClick}
            disableDuplicate={lines.length === 0}
          />
        )}
      />
      <Divider />
      <div className="grid grid-cols-2 items-end">
        <Input
          icon={config.icon}
          label={`${t('inbound.description')}`}
          disabled
          isInfoDisplay
          placeholder={config.headline}
        />
        {!readOnly && (
          <Button
            title={t('inbound.add_product')}
            icon="plus"
            onClick={onAddProductClick}
            tone={ButtonToneProps.SECONDARY}
            className="justify-self-end"
            disabled={readOnly}
          />
        )}
      </div>
      <InboundPackageTable
        packageIndex={packageIndex}
        control={control}
        errors={errors}
        linesError={!!linesError}
        setValue={setValue}
        onAddProductClick={onAddProductClick}
        readOnly={readOnly}
      />
    </div>
  );
};

// The values the form is (re)set to: the buffered data, with the supplier dropped
// while an existing order is edited (see hidePartner below).
const formValuesFor = (data: InboundFormDataStepValues, hidePartner: boolean): InboundFormDataStepValues => ({
  ...data,
  partner: hidePartner ? null : data.partner ?? null,
});

export const InboundConfiguratorForm = forwardRef<
  {
    triggerSubmit: () => void;
  },
  {
    readOnly?: boolean;
    onSubmit?: (data: InboundFormDataStepValues) => void;
    onDirtyChange?: (dirty: boolean) => void;
    onPaginationChange?: (page: number, itemsPerPage: number) => void;
    currentPage?: number;
    itemsPerPage?: number;
    totalPages?: number;
    onValidityChange?: (isValid: boolean) => void;
    inboundOrder?: InboundOrder | null;
  }
>(
  (
    {
      readOnly,
      onSubmit: onSubmitCb,
      onDirtyChange,
      onPaginationChange,
      currentPage = 1,
      itemsPerPage = 10,
      totalPages,
      onValidityChange = null,
      inboundOrder = null,
    },
    ref,
  ) => {
    const { formState, setFormState } = useInboundFormContext();
    const [partnerValid, setPartnerValid] = useState(true);
    // The supplier of an existing order is set by the backend (the merchant's own
    // partner) and is not edited here: the block is hidden while editing and the
    // value leaves the form, so its validation (company name) cannot block the save
    // and the hidden value does not count as a change.
    const hidePartner = !readOnly && Boolean(inboundOrder?.id);
    const { t } = useTranslation();
    const [indexOfPackageForWhichProductSelectionIsActive, setIndexOfPackageForWhichProductSelectionIsActive] =
      useState<number | null>(null);
    const showSelectProducts = indexOfPackageForWhichProductSelectionIsActive !== null;

    const { warehouses, isFetching } = useWarehouses();

    const {
      control,
      handleSubmit,
      setValue,
      getValues,
      reset,
      formState: { errors, isValid },
      watch,
    } = useForm<InboundFormDataStepValues>({
      resolver: zodResolver(inboundFormDataStepSchema),
      defaultValues: formValuesFor(formState.data, hidePartner),
      mode: 'onChange',
      reValidateMode: 'onChange',
    });

    const partner = watch('partner');
    const packages = useWatch({ control, name: 'packages' }) ?? [];

    // Dirty tracking against the last reset (see formDirty.ts): every value the
    // form holds, not only the registered fields.
    const watchedValues = useWatch({ control });
    const dirtySnapshotRef = useRef('');

    useEffect(() => {
      if (formState) {
        const values = formValuesFor(formState.data, hidePartner);
        reset(values);
        dirtySnapshotRef.current = serializeInboundFormValues(values);
      }
    }, [formState, hidePartner]);

    useEffect(() => {
      onValidityChange?.(isValid && partnerValid);
    }, [isValid, partnerValid]);

    useEffect(() => {
      // getValues(), not the watched values: those lag one render behind a reset()
      // and would report a change for the reset itself.
      onDirtyChange?.(isInboundFormDirty(dirtySnapshotRef.current, getValues()));
    }, [watchedValues]);

    const onSubmit = async (data: InboundFormDataStepValues) => {
      // The merchant may not know the lot at inbound time — a line without a lot is
      // valid. The WMS captures the real lot at check-in and it gets backfilled onto
      // the order line afterwards, so no lot is required here.
      setFormState(prev => ({
        ...prev,
        data,
      }));
      onSubmitCb?.(data);
    };

    useImperativeHandle(ref, () => ({
      triggerSubmit: () => {
        handleSubmit(onSubmit)();
      },
    }));

    const mainRef = useRef<HTMLDivElement>(null);

    useOutsideClick(mainRef, (e: MouseEvent) => {
      const event = e as MouseEvent;
      if (mainRef.current && !mainRef.current.contains(event.target as Node)) {
        setIndexOfPackageForWhichProductSelectionIsActive(null);
      }
    });

    const options = getPaginationOptions();

    const handleTablePaginationClick = (direction: 'previous' | 'next') => {
      let newPage = currentPage - 1;
      newPage = direction === 'next' ? newPage + 1 : newPage - 1;
      newPage = Math.max(0, Math.min(newPage, (totalPages || 1) - 1));
      onPaginationChange?.(newPage + 1, itemsPerPage);
    };

    const handlePerPageUpdate = (selectedOption: Option | null) => {
      if (selectedOption) {
        const newLimit = Number(selectedOption.value);
        onPaginationChange?.(1, newLimit);
      }
    };

    return (
      <form onSubmit={readOnly ? undefined : handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-y-32">
          <div className="grid grid-cols-1 md:grid-cols-[max-content,1fr,max-content,1fr] gap-y-32 gap-x-24 items-center">
            <div className="col-span-1 typo-text-14--bold text-faded">{t('inbound.title_label')}</div>
            <ControlledInput
              control={control}
              name="name"
              error={!!errors.name}
              errorMessage={errors.name?.message}
              disabled={readOnly}
              className="col-start-2 col-end-[-1]"
              isInfoDisplay={false}
              placeholder={t('inbound.title_label')}
            />
            <div className="col-span-1 typo-text-14--bold text-faded">{t('inbound.destination_warehouse')}</div>
            {!isFetching && warehouses && (
              <ControlledSelect
                name="warehouseId"
                control={control}
                disabled={readOnly}
                isInfoDisplay
                isMultiSelect={false}
                options={warehouses.map(warehouse => {
                  return {
                    label: `${warehouse.name} (${warehouse.country_code})`,
                    value: warehouse.id.toString(),
                  };
                })}
              />
            )}

            <div className="col-span-1 flex items-center gap-4 typo-text-14--bold text-faded">
              {t('inbound.planned_date')}
              <Tooltip title={t('inbound.planned_date_tooltip')} />
            </div>
            <DatePicker
              error={!!errors.datePlanned}
              errorMessage={errors.datePlanned?.message}
              defaultValue={dayjs(inboundOrder?.date_planned ?? formState.data.datePlanned)}
              disabled={readOnly}
              onChange={newVal => {
                if (newVal) {
                  const isoDate = dayjs(newVal || new Date()).toISOString();
                  setValue('datePlanned', isoDate, { shouldValidate: true });
                }
              }}
            />
            {!hidePartner && (
              <>
                <div className="col-span-full">
                  <Divider />
                </div>
                <div className={`flex h-full items-baseline lg:mr-48 ${!readOnly && 'pt-[7px]'}`}>
                  <div className={`typo-text-14--bold text-faded ${readOnly ? 'self-start' : ''}`}>
                    {t('inbound.partner')}
                  </div>
                </div>
                <div className="col-start-2 col-end-[-1] w-full">
                  <PartnerSelect
                    selectedPartner={partner as Partner}
                    onPartnerChange={(newPartner, isValid) => {
                      setValue('partner', newPartner as any, { shouldValidate: true });
                      setPartnerValid(isValid ?? true);
                      onValidityChange?.(isValid as unknown as boolean);
                    }}
                    domain="[('is_inbound_partner','=',True)]"
                    readOnly={readOnly}
                    errors={errors}
                    parentComponent="inbound"
                  />
                  {errors.partner?.company_name && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src="/warning-round.svg" alt="warn" className="w-[15px] h-[15px]" />
                      <span className="status-error-full text-lg font-semibold">{t('general.partner_required')}</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <Divider />

          <SectionHeadline>{t('inbound.shipment_details')}</SectionHeadline>

          {!readOnly && packages.length === 0 ? (
            <PackagesCreatorEmptyState
              onCreate={type => {
                const newPackages = structuredClone(packages);
                newPackages.push({
                  lines: [],
                  packageType: type,
                });
                setValue('packages', newPackages);
              }}
            />
          ) : (
            <>
              {readOnly && inboundOrder?.id ? (
                <>
                  <InboundSummaryTable inboundOrder={inboundOrder} />
                  <InboundPhotoGallery photos={inboundOrder.photos ?? []} />
                </>
              ) : (
                <>
                  {packages.map((singlePackage: any, idx: number) => (
                    <React.Fragment key={`package-${idx}`}>
                      {showSelectProducts && (
                        <ContainerOverlay className={styles.root} ref={mainRef} isExpanded={showSelectProducts}>
                          <ContainerHead>
                            <Button icon="keyboard-tab-right" tone={ButtonToneProps.DESTRUCTIVE_TRANSPARENT} />

                            <h3 className="typo-text-16--bold text-full truncate">{t('general.add_products')}</h3>

                            <div className="ml-auto">
                              <Button
                                icon="close"
                                onClick={() => {
                                  setIndexOfPackageForWhichProductSelectionIsActive(null);
                                }}
                                tone={ButtonToneProps.SECONDARY}
                              />
                            </div>
                          </ContainerHead>

                          <ContainerBody>
                            <SelectProductsFromAllProducts
                              showBadges={false}
                              onUpdateSuccess={products => {
                                if (indexOfPackageForWhichProductSelectionIsActive === null) {
                                  throw new Error(`No package selected for product selection`);
                                }

                                const newPackages = structuredClone(packages);

                                const newLines = products.map(product => ({
                                  quantity: 0,
                                  productId: +product.id!,
                                  labeling: 'weship' as 'weship' | 'customer' | null,
                                  appliedQualityChecks: [] as number[],
                                  lotId: null,
                                }));

                                newPackages[indexOfPackageForWhichProductSelectionIsActive].lines?.push(...newLines);

                                setValue('packages', newPackages);
                                setIndexOfPackageForWhichProductSelectionIsActive(null);
                              }}
                            />
                          </ContainerBody>
                        </ContainerOverlay>
                      )}

                      <PackagesCreatorEditor
                        packageType={singlePackage.packageType}
                        onAddProductClick={() => setIndexOfPackageForWhichProductSelectionIsActive(idx)}
                        onDeleteClick={() => {
                          const newPackages = structuredClone(packages);
                          newPackages.splice(idx, 1);
                          setValue('packages', newPackages);
                        }}
                        onDuplicateClick={() => {
                          const newPackages = structuredClone(packages);
                          const cloned = structuredClone(newPackages[idx]);
                          cloned.name = cloned.name ? `${cloned.name} (${t('general.copy')})` : '';
                          newPackages.splice(idx + 1, 0, cloned);
                          setValue('packages', newPackages);
                        }}
                        packageIndex={idx}
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        readOnly={readOnly}
                      />
                    </React.Fragment>
                  ))}
                </>
              )}

              {totalPages && totalPages > 1 && (
                <div className="flex justify-between items-center mt-16">
                  <div className="flex gap-x-10 flex-1 items-center">
                    <div>
                      <Select
                        onChange={handlePerPageUpdate}
                        options={options}
                        defaultValue={options.find(opt => opt.value === String(itemsPerPage)) || options[1]}
                        menuPlacement="auto"
                      />
                    </div>
                    <div className="typo-text-12 text-faded">{t('general.per_page')}</div>
                  </div>
                  <div className="ml-auto">
                    <div className="w-full my-20 flex items-center">
                      <div className="ml-auto">
                        <TablePagination
                          maxPage={totalPages || 0}
                          currentPage={currentPage - 1}
                          onPaginationClick={handleTablePaginationClick}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {!readOnly && (
                <div className="flex flex-wrap gap-24 justify-center">
                  <Button
                    title={t('inbound.add_parcel')}
                    icon="package"
                    tone={ButtonToneProps.SECONDARY}
                    disabled={readOnly}
                    onClick={() => {
                      const newPackages = structuredClone(packages);
                      newPackages.push({
                        lines: [],
                        packageType: 'parcel',
                      });
                      setValue('packages', newPackages);
                    }}
                  />
                  <Button
                    title={t('inbound.add_pallet')}
                    icon="pallet"
                    tone={ButtonToneProps.SECONDARY}
                    disabled={readOnly}
                    onClick={() => {
                      const newPackages = structuredClone(packages);
                      newPackages.push({
                        lines: [],
                        packageType: 'pallet',
                      });
                      setValue('packages', newPackages);
                    }}
                  />
                </div>
              )}
            </>
          )}
          {errors.packages && (errors.packages.message || errors.packages.root?.message) && (
            <div className="typo-text-14 flex gap-4 status-error-full justify-center">
              <Icon name="error" />
              <div className="content-center">{errors.packages.message || errors.packages.root?.message}</div>
            </div>
          )}
        </div>
      </form>
    );
  },
);
